import { generateColors } from "@mantine/colors-generator";
import { alpha, createTheme, rem } from "@mantine/core";
import { create } from "zustand";
import { formatThousand } from "../functions/utilFunctions";

const defaultPrimaryColor = "#8963F4";
const defaultSecondaryColor = "#F5C542";
const defaultGreenColor = "#3FC856";
const defaultBlueColor = "#4895ff";

export const lemonColor = "#F3FF70"

function generatePrimaryColor(c) {
  if (c === defaultPrimaryColor) {
    return [
      "#E9DCFF",
      "#F2EAFF",
      "#f9f5ff", //App background
      "#E9DCFF",
      "#8963F4",
      "#8963F4",
      "#8963F4",
      "#8963F4",
      "#f1e9fe", //Login background right
      "#3a2b60", //Login background left
    ]
  }
  let result = generateColors(c)
  result[2] = alpha(c, .03)
  result[6] = c

  return result
}

function generateSecondaryColor(c) {
  let result = generateColors(c)
  result[6] = c

  if (c === defaultSecondaryColor) {
    result[3] = lemonColor
  }
  else {
    result[3] = c
  }

  return result
}

const setupTheme = (
  primaryColor,
  secondaryColor,
  greenColor,
  blueColor,
  tertiaryColor = "#0caf49"
) => {
  return createTheme({
    scale: .9,
    colors: {
      primary: generatePrimaryColor(primaryColor),
      secondary: generateSecondaryColor(secondaryColor),
      green: generateColors(greenColor),
      blue: generateColors(blueColor),
      tertiary: [
        alpha(tertiaryColor, 0.1),
        alpha(tertiaryColor, 0.2),
        alpha(tertiaryColor, 0.3),
        alpha(tertiaryColor, 0.4),
        alpha(tertiaryColor, 0.6),
        alpha(tertiaryColor, 1),
        alpha(tertiaryColor, 1),
        alpha(tertiaryColor, 1),
        alpha(tertiaryColor, 1),
        alpha(tertiaryColor, 1),
      ],
    },
    primaryColor: "primary",
    primaryShade: 6,
    fontFamily: "Inter",
    fontSizes: {
      xs: rem(10),
      sm: rem(12),
      md: rem(14),
      lg: rem(16),
      xl: rem(20),
    },
    components: {
      BarChart: {
        defaultProps: {
          valueFormatter: formatThousand
        }
      },
      ThemeIcon: {
        defaultProps: {
          variant: "subtle",
          size: 24,
        },
      },
      Indicator: {
        styles: {
          indicator: {
            zIndex: 10
          },
        },
      },
      NumberFormatter: {
        defaultProps: {
          thousandSeparator: " "
        },
      },
      Paper: {
        defaultProps: {
          radius: "md",
          bg: primaryColor === defaultPrimaryColor ? "#fcfbff" : "#fff"
        },
      },
      Tooltip: {
        defaultProps: {
          maw: 300,
          multiline: true,
          zIndex: 9999,
        },
      },
      Combobox: {
        defaultProps: {
          keepMounted: false,
          zIndex: 1500,
        },
      },
      Popover: {
        defaultProps: {
          shadow: "md",
          zIndex: 120,
          radius: "md"
        },
      },
      Modal: {
        styles: {
          title: {
            lineHeight: 1.5,
          },
        },
        defaultProps: {
          overlayProps: {
            blur: 1
          },
          centered: true,
        },
      },
      Text: {
        defaultProps: {
          fw: 400,
          ff: "Inter",
          // truncate: "end"
        },
      },
      Anchor: {
        defaultProps: {
          fw: 500,
          ff: "Inter",
        },
      },
      LoadingOverlay: {
        defaultProps: {
          zIndex: 100,
        },
      },
      Button: {
        defaultProps: {
          radius: "md"
        }
      },
      ActionIcon: {
        defaultProps: {
          radius: "md"
        }
      },
      ColorInput: {
        defaultProps: {
          radius: "md"
        },
        styles: {
          input: {
            borderColor: "var(--mantine-color-primary-0)",
          },
        }
      },
      TextInput: {
        defaultProps: {
          radius: "md",
        },
        styles: {
          input: {
            borderColor: "var(--mantine-color-primary-0)",
          },
        }
      },
      PasswordInput: {
        defaultProps: {
          radius: "md",
        },
        styles: {
          input: {
            borderColor: "var(--mantine-color-primary-0)",
          },
        }
      },
      Textarea: {
        defaultProps: {
          radius: "md",
        },
        styles: {
          input: {
            borderColor: "var(--mantine-color-primary-0)",
          },
        }
      },
      Select: {
        defaultProps: {
          radius: "md",
        },
        styles: {
          input: {
            borderColor: "var(--mantine-color-primary-0)",
          },
        }
      },
      TagsInput: {
        defaultProps: {
          radius: "md"
        },
        styles: {
          input: {
            borderColor: "var(--mantine-color-primary-0)",
          },
        }
      },
      InputBase: {
        defaultProps: {
          radius: "md"
        },
        styles: {
          input: {
            borderColor: "var(--mantine-color-primary-0)",
          },
        }
      },
      SegmentedControl: {
        defaultProps: {
          radius: "md",
          bg: primaryColor === defaultPrimaryColor ? "#fcfbff" : "#fff",
          color: primaryColor === defaultPrimaryColor ? "#e9dcff" : "primary",
          style: {
            border: primaryColor === defaultPrimaryColor ? "1px solid #e9dcff" : "1px solid var(--mantine-color-primary-0)",
          },
        },
        styles: {
          label: {
            color: primaryColor === defaultPrimaryColor ? "#000" : undefined,
          },
        }
      },
      Checkbox: {
        defaultProps: {
          radius: "md"
        },
      },
      Divider: {
        defaultProps: {
          color: "var(mantine-color-primary-0)"
        },
      },
      Accordion: {
        defaultProps: {
          radius: "md"
        },
      },
      CloseButton: {
        defaultProps: {
          radius: "md"
        },
      },
    },
    cursorType: "pointer",
    white: primaryColor === defaultPrimaryColor ? "#fcfbff" : "#fff"
  });
};

const themeStore = (set) => ({
  theme: setupTheme(defaultPrimaryColor, defaultSecondaryColor, defaultGreenColor, defaultBlueColor),
  setTheme: (primary, secondary, green, blue) =>
    set({
      theme: setupTheme(
        primary || defaultPrimaryColor,
        secondary || defaultSecondaryColor,
        green || defaultGreenColor,
        blue || defaultBlueColor
      ),
    }),
  asideCollapsed: false,
  setAsideCollapsed: (newData) => set({ asideCollapsed: newData }),

  actionColors: {
    email: defaultSecondaryColor,
    linkedin_visit: defaultBlueColor,
    linkedin_invitation: "#20b338ff",
    linkedin_message: defaultBlueColor,
    event: "#b120ff",
    sms: defaultGreenColor,
    smv: "#00cdd8",
    call: "#222222",
    split_contacts: "#f5c542",
    remove_prospect: "#ff3131",
  },
  setActionColor: (newData) => set({ actionColors: newData }),
});

export const useThemeStore = create(themeStore);
